// Client-side mirror of the API's role → scope ceiling
// (`apps/api/src/middleware/auth.ts`).
//
// ONE rule, three call sites. Every per-repo/account write the console offers
// sits in `OWNER_SCOPES` alone — `write:webhooks` (fire a signed delivery at
// someone's endpoint), `write:issues` (override someone's triage), `write:repos`
// (re-check their policy). An admin runs billing and the fleet; none of those
// three is billing. So "roles that plainly lack an owner write" is one list, not
// one list per feature that can drift apart.
//
// The server stays the authority — this only hides a control that would plainly
// 403. An ABSENT role is not proof of insufficiency: `/v1/me` sends no `role`
// for a PAT or another machine credential, so an unknown caller KEEPS the control.
// A wrongly-shown control costs one error message; a wrongly-hidden one hides
// the only manual lever over the loop.

/** Roles below every owner-only write. Cumulative ladder: owner ≫ admin ≫ member ≫ viewer. */
const NON_OWNER_WRITE_ROLES: readonly string[] = ['admin', 'member', 'viewer'];

/** Whether to offer this caller an owner-only write. Unknown role ⇒ yes. */
export const canOwnerWrite = (role: string | null | undefined): boolean =>
  !NON_OWNER_WRITE_ROLES.includes(role ?? '');

/**
 * Roles below the ADMIN ceiling (`ADMIN_SCOPES`, `apps/api/src/middleware/auth.ts`).
 * Same cumulative ladder, one rung lower than {@link canOwnerWrite}.
 */
const NON_ADMIN_WRITE_ROLES: readonly string[] = ['member', 'viewer'];

/**
 * Whether to offer this caller an ADMIN-ceiling write — the account-config class
 * the API groups with billing: `write:billing`, `write:donations`,
 * `write:integrations`, `write:runners`, `write:merge:gate` (the Merge Gate hold, #7011),
 * `write:squads` (naming squads of repos, #7010), `write:members`, `write:prompts`. An
 * admin runs those; only the owner writes repos / issues / webhooks / secrets
 * ({@link canOwnerWrite}).
 *
 * Absent role ⇒ yes, for {@link canOwnerWrite}'s reason and no other: `/v1/me`
 * sends no `role` for a PAT, and a wrongly-hidden control costs more than a
 * wrongly-shown one.
 */
export const canAdminWrite = (role: string | null | undefined): boolean =>
  !NON_ADMIN_WRITE_ROLES.includes(role ?? '');

/**
 * Whether to offer this caller a TASK write: New task (`POST /v1/tasks`) and Stop
 * (`POST /v1/tasks/:id/cancel`), both `write:tasks`, which members hold and viewers
 * do not (`VIEWER_SCOPES` is `read:*` only). Owner ruling G12 (#6788): the queue
 * keeps its role behaviour on `/fleet/work`, so only a viewer loses them. G21's
 * admin-only rule governs machine actions ({@link canAdminWrite}), not tasks.
 *
 * Absent role ⇒ yes, for {@link canOwnerWrite}'s reason.
 */
export const canWriteTasks = (role: string | null | undefined): boolean => role !== 'viewer';

/**
 * Whether to show this caller ANY credential-vault surface: the account vault
 * (`GET|POST /v1/secrets`, `DELETE /v1/secrets/:name`) and the per-repo one
 * (`GET|PUT /v1/repos/:owner/:repo/secrets`, `DELETE …/:name`). Both scopes moved to
 * OWNER-ONLY (#1418) — `read:secrets` is carved out of the viewer's `read:*`
 * sweep and `write:secrets` out of the admin ceiling — so all six routes now 403
 * for viewer / member / admin, where the read used to serve every role.
 *
 * Same predicate as `canOwnerWrite`, deliberately its own NAME, the
 * `canManageWebhooks` / `canForceIssueActions` precedent: this one gates a READ,
 * and it is the METADATA that is sensitive. Knowing a repo carries a
 * `STRIPE_LIVE_KEY` in `prod` is itself the disclosure the API-side carve-out
 * names, so "render the list, hide the buttons" is not enough — the whole
 * surface goes, and the component that would fetch it never mounts.
 *
 * The absent-role rule is `canOwnerWrite`'s, unchanged and load-bearing here:
 * `/v1/me` sends no `role` for a PAT, which carries its own scopes and may well
 * hold `read:secrets`. So an absent role KEEPS the surface — the server is still
 * the authority, and hiding it would blind a caller the API serves.
 *
 * An absent role is an ANSWER. An UNRESOLVED `/v1/me` is not one — see
 * {@link roleSettled}, which every secrets call site waits on.
 */
export const canAccessSecrets = canOwnerWrite;

/**
 * Has `/v1/me` answered for the CURRENT tenant? Only then may a secrets surface
 * paint. Two states that are NOT settled and that `!isLoading` lets through:
 *
 *   - **Carried-over placeholder.** `useMe` keeps `placeholderData` across an org
 *     switch by design (it stops a Router unmount cycle — see `lib/session.ts`),
 *     so `isLoading` goes false while `role` is still the PREVIOUS org's. For a
 *     write that flashes a dead button; for the vault it flashes ANOTHER TENANT's
 *     secret-name list, on a platform whose whole point is that org A's people
 *     cannot see org B. `isPlaceholderData` is the only field that tells the
 *     carried answer apart from the fresh one — both report `status: 'success'`.
 *   - **Failed.** `isSuccess` is not `!isLoading`: an errored query has both
 *     false, and a failed REFETCH keeps its stale `data` while flipping
 *     `isSuccess` off. Either way the role we hold is not an answer.
 *
 * Structurally typed rather than taking the query: the rule is about two booleans,
 * and this stays callable from a test without a QueryClient.
 */
export const roleSettled = (me: {
  readonly isSuccess: boolean;
  readonly isPlaceholderData: boolean;
}): boolean => me.isSuccess && !me.isPlaceholderData;
